import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { FirmaCanvas } from './FirmaCanvas';
import { FirmaSubirFoto } from './FirmaSubirFoto';
import { KeyRound, CheckCircle2, FileText, Camera, Pen } from 'lucide-react';

interface FirmaData {
  candidato_id: number;
  existe: boolean;
  acepta_notificaciones?: boolean;
  email_notificaciones?: string | null;
  telefono_notificaciones?: string | null;
  canal?: string | null;
  firma_imagen_url?: string | null;
  firma_aceptada?: boolean;
  firma_aceptada_at?: string | null;
  declaracion_version?: string;
  declaracion_vigente: {
    version: string;
    texto: string;
  };
}

export function FirmaTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<FirmaData>({
    queryKey: ['mi-firma'],
    queryFn: async () => (await api.get<FirmaData>('/me/firma')).data,
  });

  const [acepta, setAcepta] = useState(false);
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [canal, setCanal] = useState<string>('email');
  const [firmaDataUrl, setFirmaDataUrl] = useState<string | null>(null);
  const [acepto, setAcepto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modo, setModo] = useState<'foto' | 'dibujo'>('foto');

  useEffect(() => {
    if (data?.existe) {
      setAcepta(!!data.acepta_notificaciones);
      setEmail(data.email_notificaciones ?? '');
      setTelefono(data.telefono_notificaciones ?? '');
      setCanal(data.canal ?? 'email');
    }
  }, [data]);

  const mut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/me/firma', {
        acepta_notificaciones: acepta,
        email_notificaciones: email || null,
        telefono_notificaciones: telefono || null,
        canal,
        firma_data_url: firmaDataUrl,
        acepto_declaracion: acepto,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mi-firma'] }),
    onError: (e: any) => setError(e?.response?.data?.detail || 'Error al registrar firma'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!firmaDataUrl) {
      setError(modo === 'foto' ? 'Debes subir o tomar la foto de tu firma antes de guardar' : 'Debes dibujar tu firma antes de guardar');
      return;
    }
    if (!acepto) {
      setError('Debes aceptar la declaración');
      return;
    }
    mut.mutate();
  };

  if (isLoading || !data) return <div className="p-4 text-sm text-gray-500">Cargando…</div>;

  return (
    <section className="space-y-4">
      <header className="bg-white border rounded p-4">
        <div className="flex items-center gap-2 text-arpp-primary">
          <KeyRound size={20} />
          <h3 className="text-sm font-bold">Notificaciones electrónicas y firma digital</h3>
        </div>
        <p className="text-xs text-gray-600 mt-1">
          Registra tu firma autorizada. Será usada en los documentos que el PLT
          presentará por ti ante el JEE vía DECLARA+.
        </p>
      </header>

      {data.existe && data.firma_aceptada && (
        <div className="bg-green-50 border border-green-300 rounded p-4">
          <div className="flex items-center gap-2 text-green-800 mb-2">
            <CheckCircle2 size={18} />
            <strong className="text-sm">Firma registrada</strong>
            <span className="text-xs text-gray-600">
              {data.firma_aceptada_at && `· ${new Date(data.firma_aceptada_at).toLocaleString('es-PE')}`}
            </span>
          </div>
          {data.firma_imagen_url && (
            <div className="border bg-white rounded p-2 mt-2 inline-block">
              <img
                src={data.firma_imagen_url}
                alt="Firma actual"
                className="max-h-24"
              />
            </div>
          )}
          <p className="text-[11px] text-gray-600 mt-2">
            Puedes actualizarla si lo deseas, completando el formulario nuevamente.
          </p>
        </div>
      )}

      <form onSubmit={onSubmit} className="bg-white border rounded p-4 space-y-4">
        <h4 className="text-sm font-semibold">
          {data.existe ? 'Actualizar firma' : 'Registrar firma por primera vez'}
        </h4>

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-gray-700">Notificaciones electrónicas</legend>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={acepta}
              onChange={(e) => setAcepta(e.target.checked)}
            />
            Acepto recibir notificaciones electrónicas con plenos efectos legales
          </label>
          <div className="grid md:grid-cols-3 gap-2 mt-1">
            <div>
              <label className="block text-[11px] text-gray-600">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
                placeholder="ejemplo@correo.com"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-600">Teléfono (opcional)</label>
              <input
                type="tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
                placeholder="9XX XXX XXX"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-600">Canal preferido</label>
              <select
                value={canal}
                onChange={(e) => setCanal(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              >
                <option value="email">Email</option>
                <option value="casilla_jne">Casilla electrónica JNE</option>
                <option value="ambos">Ambos</option>
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-gray-700">Tu firma</legend>

          <div className="flex gap-1 border-b">
            <button
              type="button"
              onClick={() => { setModo('foto'); setFirmaDataUrl(null); }}
              className={`px-3 py-1.5 text-xs border-b-2 -mb-px inline-flex items-center gap-1 ${
                modo === 'foto'
                  ? 'border-arpp-primary text-arpp-primary font-medium'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Camera size={12} /> Subir foto de firma <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1 rounded">RECOMENDADO</span>
            </button>
            <button
              type="button"
              onClick={() => { setModo('dibujo'); setFirmaDataUrl(null); }}
              className={`px-3 py-1.5 text-xs border-b-2 -mb-px inline-flex items-center gap-1 ${
                modo === 'dibujo'
                  ? 'border-arpp-primary text-arpp-primary font-medium'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Pen size={12} /> Dibujar (mouse/dedo)
            </button>
          </div>

          {modo === 'foto' ? (
            <FirmaSubirFoto
              onChange={setFirmaDataUrl}
              initialDataUrl={data.firma_imagen_url}
            />
          ) : (
            <FirmaCanvas
              onChange={setFirmaDataUrl}
              initialDataUrl={data.firma_imagen_url}
            />
          )}
        </fieldset>

        <fieldset className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2 text-xs">
          <div className="flex items-center gap-1 font-semibold text-blue-900">
            <FileText size={13} /> Declaración ({data.declaracion_vigente.version})
          </div>
          <p className="text-blue-900 italic">"{data.declaracion_vigente.texto}"</p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={acepto}
              onChange={(e) => setAcepto(e.target.checked)}
              className="mt-0.5"
            />
            <span>Acepto la declaración y autorizo el uso de la firma registrada arriba en los documentos electorales que se presenten en mi nombre ante los JEE vía DECLARA+.</span>
          </label>
        </fieldset>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={mut.isPending || !firmaDataUrl || !acepto}
          className="w-full bg-arpp-primary text-white py-2.5 rounded font-medium hover:bg-blue-800 disabled:opacity-50"
        >
          {mut.isPending ? 'Guardando…' : data.existe ? 'Actualizar firma y declaración' : 'Aceptar y registrar firma'}
        </button>
      </form>
    </section>
  );
}
